/**
 * ============================================================================
 * AUTHENTICATION CONTEXT - DUAL AUTH SYSTEM
 * ============================================================================
 * 
 * MEDD-SIM supports TWO authentication methods:
 * 
 * 1. WORDPRESS SSO (Master Tenant Only - sim.medd.com.au)
 *    - validateToken() handles JWT tokens from medd.com.au
 *    - Token stored in localStorage as 'medd_token'
 *    - Creates deterministic Supabase password from WordPress ID
 * 
 * 2. DIRECT SUPABASE AUTH (All White-Label Tenants)
 *    - loadUserFromSupabaseSession() handles direct email/password login
 *    - Used by: GSK, Boston Scientific, Edwards, and all other tenants
 *    - NO WordPress involvement - pure Supabase Auth
 * 
 * The checkAuth() function determines which method to use:
 *    - If WordPress token exists → WordPress SSO flow
 *    - If no token → Check for Supabase session (white-label)
 * 
 * ⚠️  White-label tenants must NEVER be redirected to medd.com.au
 * ⚠️  WordPress SSO is ONLY for the MEDD master tenant
 * 
 * See: docs/AUTHENTICATION.md for full documentation
 * ============================================================================
 */

import { MASTER_TENANT_ID } from '@/types/tenant';
import { wordpressExchangeRoute, enforceLegacySignInResult } from '@/lib/auth/wordpressExchangeRouting';
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { setSentryUser, clearSentryUser, captureAuthError } from '@/lib/sentry';
import { resolveAuthProfile } from '@/lib/profileSelection';
import { hasActiveTenantMembership } from '@/lib/tenantMembershipAccess';

interface User {
  id: string;
  supabaseAuthId?: string; // Supabase Auth UID (different from profile id)
  email: string;
  name: string;
  roles: string[];
  company_id?: string;
  company_name?: string;
  avatar_url?: string;
  status: string;
  medd_sim_user?: boolean;
  profile_complete?: boolean;
  user_type?: 'hcp' | 'non_hcp'; // HCP or Non-HCP user type
  is_hcp?: boolean; // Convenience flag for HCP check
  tenant_id?: string; // Tenant ID for white-label isolation
}

interface Profile {
  id: string;
  auth_user_id: string | null;
  company_id: string | null;
  email: string;
  display_name: string | null;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to map WordPress roles to app roles (legacy - used as fallback)
const mapWordPressRoleToAppRole = (wpRoles: string[] | string | undefined): 'admin' | 'company_admin' | 'user' => {
  if (!wpRoles) return 'user';
  
  const roles = Array.isArray(wpRoles) ? wpRoles : [wpRoles];
  
  // WordPress administrator → admin
  if (roles.includes('administrator')) {
    return 'admin';
  }
  
  // WordPress editor/author → company_admin
  if (roles.includes('editor') || roles.includes('author')) {
    return 'company_admin';
  }
  
  // Everyone else → user
  return 'user';
};

// Helper function to find or create company by name using RPC (bypasses RLS)
async function findOrCreateCompanyByName(companyName: string): Promise<{ id: string; name: string; isNew: boolean } | null> {
  if (!companyName || companyName.trim() === '') {
    return null;
  }

  const trimmedName = companyName.trim();

  try {
    // Use the security definer RPC function to bypass RLS
    const { data, error } = await supabase.rpc('resolve_company_by_name', {
      p_company_name: trimmedName
    });

    if (error) {
      console.error('❌ Company RPC error:', error);
      return null;
    }

    // The RPC returns a JSONB object
    const result = data as { success: boolean; id?: string; name?: string; is_new?: boolean; error?: string };
    
    if (!result.success) {
      console.error('❌ Company resolution failed:', result.error);
      return null;
    }

    return { 
      id: result.id!, 
      name: result.name!, 
      isNew: result.is_new || false 
    };
  } catch (error) {
    console.error('❌ findOrCreateCompanyByName RPC failed:', error);
    return null;
  }
}

// Helper function to authenticate Supabase user with deterministic password
async function authenticateSupabaseUser(email: string, wordpressId: string, profileId: string) {
  try {
    // Create deterministic password from WordPress ID
    const password = `wp-sso-${wordpressId}-medd-platform-2025`;

    // Try to sign in first
    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (signInError) {
      // If user doesn't exist in Supabase Auth, create them
      if (signInError.message.includes('Invalid login credentials') || 
          signInError.message.includes('Email not confirmed')) {
        
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              wordpress_id: wordpressId,
              profile_id: profileId, // Link to profile for reference
              created_via: 'wordpress_sso',
            },
            emailRedirectTo: undefined, // Skip email verification
          }
        });

        if (signUpError) {
          console.error('❌ Supabase auth sign-up failed:', signUpError);
          throw signUpError;
        }

        return signUpData;
      } else {
        throw signInError;
      }
    }

    return signInData;
  } catch (error) {
    console.error('❌ Supabase authentication failed:', error);
    throw error;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const validateToken = async (token: string): Promise<User> => {
    try {
      // Step 1: Validate WordPress token
      const formData = new URLSearchParams();
      formData.append('token', token);
      
      const response = await fetch(
        'https://medd.com.au/wp-admin/admin-ajax.php?action=medd_validate_token',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: formData
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success || !data.data.valid) {
        throw new Error(data.data?.message || 'Token validation failed');
      }

      const wpUser = data.data.user;
      
      // 🔍 DEBUG: Log the FULL WordPress response to see all available fields
      
      // 🆕 Read MEDD-SIM specific fields from JWT
      // Accept truthy values, not just strict boolean true
      const meddSimUser = !!wpUser.medd_sim_user;
      
      // 🏢 Expanded company name detection - check ALL possible field names
      const meddSimCompanyName = 
        wpUser.medd_sim_company_name || 
        wpUser.sim_company_name || 
        wpUser.company_name ||
        wpUser.sim_company ||
        wpUser.company ||
        wpUser.medd_company_name ||
        wpUser.medd_company ||
        null;
      
      const meddSimRole = wpUser.medd_sim_role || wpUser.sim_role || null; // 'user' | 'company_admin' | null
      
      // 🆕 Read User Type (HCP/Non-HCP) - defaults to 'hcp' if not set
      const meddSimUserType = wpUser.medd_sim_user_type || wpUser.user_type || 'hcp';
      const isHcp = wpUser.is_hcp !== undefined ? wpUser.is_hcp : (meddSimUserType === 'hcp');
      
      
      // 🏢 Log ALL company-related fields from WordPress for debugging
      

      // Step 2: Authenticate to Supabase FIRST (before profile query)
      // This ensures auth.uid() is available for RLS policies
      try {
        
        let legacySignInOnly = false;
        if (import.meta.env.VITE_WORDPRESS_SESSION_EXCHANGE_ENABLED === 'true') {
          const { data: exchange, error: exchangeError } = await supabase.functions.invoke('wordpress-session-exchange', { body: { token } });
          let noLinkedAccount = false;
          if (exchangeError) {
            const response = exchangeError.context;
            const details = response instanceof Response ? await response.json().catch(() => null) : null;
            const route = wordpressExchangeRoute(response instanceof Response ? response.status : undefined, details?.code);
            noLinkedAccount = route === 'register-if-new';
            legacySignInOnly = route === 'sign-in-only';
            if (route === 'deny') throw new Error(typeof details?.error === 'string' ? details.error : 'Unable to reconnect your MEDD account. Please contact support.');
          }
          if (!noLinkedAccount && !legacySignInOnly) {
            if (typeof exchange?.access_token !== 'string' || typeof exchange?.refresh_token !== 'string') throw new Error('Invalid sign-in response.');
            const { error: sessionError } = await supabase.auth.setSession(exchange);
            if (sessionError) throw sessionError;
            const restoredUser = await loadUserFromSupabaseSession();
            if (restoredUser) return restoredUser;
            // Clear the exchange session before a password attempt can fail.
            const { error: cleanupError } = await supabase.auth.signOut({ scope: 'local' });
            if (cleanupError) throw cleanupError;
            // Client-only linked identity: retain the old password path, but
            // never create another account or infer a new membership here.
            legacySignInOnly = true;
          }
          // Only a trusted server NO_LINKED_ACCOUNT response may use the
          // existing first-time registration flow. Never an existing identity.
        }

        // Create deterministic password from WordPress ID
        const password = `wp-sso-${wpUser.id}-medd-platform-2025`;
        
        // Try to sign in first
        let authResult = await supabase.auth.signInWithPassword({
          email: wpUser.email,
          password,
        });
        
        enforceLegacySignInResult(authResult, legacySignInOnly);
        if (authResult.error) {
          // If user doesn't exist in Supabase Auth, create them
          if (authResult.error.message.includes('Invalid login credentials') || 
              authResult.error.message.includes('Email not confirmed')) {
            
            const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
              email: wpUser.email,
              password,
              options: {
                data: {
                  wordpress_id: wpUser.id,
                  created_via: 'wordpress_sso',
                },
                emailRedirectTo: undefined,
              }
            });
            
            if (signUpError) {
              console.error('❌ Supabase auth sign-up failed:', signUpError);
              throw signUpError;
            }
            
            if (!signUpData.session) {
              throw new Error('Please confirm your email using the signup email, then return to MEDD and open the simulator again.');
            }
            authResult = { data: signUpData, error: null };
          } else {
            throw authResult.error;
          }
        }
        
        
        // Get the authenticated user
        const { data: { user: authUser }, error: authUserError } = await supabase.auth.getUser();
        
        if (authUserError || !authUser) {
          console.error('❌ Failed to get auth user after login:', authUserError);
          throw new Error('Authentication failed');
        }
        
        
        // Step 2b: NOW query for profile (RLS will allow via email match)
        
        // 🔍 IMPROVED MATCHING: First try email, then WordPress ID
        // This prevents duplicate profiles when users are pre-created via invite
        let profile = null;
        let profileError = null;
        
        // Step 1: Try to find by email first (catches pre-created/invited users)
        const { data: emailProfiles, error: emailError } = await supabase
          .from('profiles')
          .select(`
            *,
            companies (
              id,
              name
            )
          `)
          .eq('email', wpUser.email.toLowerCase())
          .order('created_at', { ascending: false });
        
        if (emailError) {
          console.error('Email profile fetch error:', emailError);
          profileError = emailError;
        } else if (emailProfiles && emailProfiles.length > 0) {
          profile = emailProfiles[0];
          
          // 🔗 LINK: Update WordPress ID if not set (invited user first login)
          if (!profile.wordpress_user_id) {
            await supabase
              .from('profiles')
              .update({ 
                wordpress_user_id: wpUser.id.toString(),
                sso_provider: 'wordpress'
              })
              .eq('id', profile.id);
            profile.wordpress_user_id = wpUser.id.toString();
          }
          
          // Handle duplicate detection - merge if needed
          if (emailProfiles.length > 1) {
            console.warn(`⚠️ Found ${emailProfiles.length} profiles for email ${wpUser.email}. Using primary and logging duplicates.`);
            // Log duplicate IDs for admin cleanup
            const duplicateIds = emailProfiles.slice(1).map(p => p.id);
            console.warn('🔧 Duplicate profile IDs to clean up:', duplicateIds);
          }
        }
        
        // Step 2: Fallback to WordPress ID lookup if no email match
        if (!profile && !profileError) {
          const { data: wpProfiles, error: wpError } = await supabase
            .from('profiles')
            .select(`
              *,
              companies (
                id,
                name
              )
            `)
            .eq('wordpress_user_id', wpUser.id.toString())
            .order('created_at', { ascending: false });
          
          if (wpError) {
            console.error('WordPress profile fetch error:', wpError);
            profileError = wpError;
          } else if (wpProfiles && wpProfiles.length > 0) {
            profile = wpProfiles[0];
          }
        }

        // ✅ Check for query errors
        if (profileError) {
          console.error('Profile fetch error:', profileError);
          throw profileError;
        }
        
        if (profile) {
        } else {
        }

        // 🆕 Handle company resolution with multiple fallbacks
        let resolvedCompanyId = wpUser.company_id || profile?.company_id || null;
        let resolvedCompanyName = wpUser.company_name || (profile as any)?.companies?.name || null;
        
        // Try to resolve company from MEDD-SIM fields or any company name field (already expanded above)
        const companyNameToResolve = meddSimCompanyName;
        
        if (companyNameToResolve && !resolvedCompanyId) {
          const companyResult = await findOrCreateCompanyByName(companyNameToResolve);
          if (companyResult) {
            resolvedCompanyId = companyResult.id;
            resolvedCompanyName = companyResult.name;
          }
        }
        
        // 🏢 FORCE company lookup for EXISTING profiles that have no company assigned
        if (companyNameToResolve && profile && !profile.company_id && !resolvedCompanyId) {
          const companyResult = await findOrCreateCompanyByName(companyNameToResolve);
          if (companyResult) {
            resolvedCompanyId = companyResult.id;
            resolvedCompanyName = companyResult.name;
          }
        }
        
        if (!resolvedCompanyId && !companyNameToResolve) {
          console.warn('⚠️ No company information found in WordPress JWT');
        }

        // If profile doesn't exist, create it
        if (!profile) {
            
            // 🔒 DUPLICATE PREVENTION: Check one more time for existing profiles
            const { data: existingProfiles } = await supabase
              .from('profiles')
              .select('id, username, auth_user_id, created_at')
              .eq('wordpress_user_id', wpUser.id.toString())
              .order('created_at', { ascending: false });
            
            if (existingProfiles && existingProfiles.length > 0) {
              
              // Use the newest existing profile
              const existingProfile = existingProfiles[0];
              
              // Get full profile data with company info
              const { data: fullProfile } = await supabase
                .from('profiles')
                .select(`
                  *,
                  companies (
                    id,
                    name
                  )
                `)
                .eq('id', existingProfile.id)
                .single();
              
              if (fullProfile) {
                // Continue with existing profile (will handle auth below)
                const profile = fullProfile;
                
                // 🔐 AUTHENTICATION: Create/restore Supabase auth session
                try {
                  await authenticateSupabaseUser(profile.email, wpUser.id.toString(), profile.id);
                  
                  // 🔗 Link auth_user_id to profile for RLS
                  const { data: { user: linkedAuthUser } } = await supabase.auth.getUser();
                  if (linkedAuthUser && (!profile.auth_user_id || profile.auth_user_id !== linkedAuthUser.id)) {
                    await supabase
                      .from('profiles')
                      .update({ auth_user_id: linkedAuthUser.id })
                      .eq('id', profile.id);
                  }
                } catch (authError) {
                  console.error('⚠️ Supabase authentication failed:', authError);
                  throw authError;
                }
                
                // Get auth user and continue with role handling
                const { data: { user: authUser }, error: authUserError } = await supabase.auth.getUser();
                
                if (authUserError || !authUser) {
                  console.error('❌ Failed to get auth user:', authUserError);
                  throw new Error('Authentication failed');
                }
                
                // Fetch and sync roles (same logic as existing profile path)
                const { data: userRoles, error: rolesError } = await supabase
                  .from('user_roles')
                  .select('role')
                  .eq('user_id', authUser.id);
                
                let roles = userRoles?.map(r => r.role) || ['user'];
                
                // 🆕 Use medd_sim_role if provided (takes priority over WP role mapping)
                const roleToSync = meddSimRole || wpUser.sim_role;
                if (roleToSync) {
                  const targetRole = roleToSync === 'company_admin' ? 'company_admin' : 
                                     roleToSync === 'admin' ? 'admin' : 'user';
                  
                  if (!roles.includes(targetRole)) {
                    await supabase.from('user_roles').delete().eq('user_id', authUser.id);
                    await supabase.from('user_roles').insert({
                      user_id: authUser.id,
                      role: targetRole,
                    });
                    roles = [targetRole];
                  }
                } else if (wpUser.roles) {
                  // Legacy: Sync role if needed - but PRESERVE manually assigned higher roles
                  const expectedRole = mapWordPressRoleToAppRole(wpUser.roles);
                  const currentRoleSet = new Set(roles);
                  
                  // Role hierarchy: admin > company_admin > user
                  const roleHierarchy: Record<string, number> = { 'admin': 3, 'company_admin': 2, 'user': 1 };
                  const currentHighestRole = roles.reduce((highest, role) => {
                    return (roleHierarchy[role] || 0) > (roleHierarchy[highest] || 0) ? role : highest;
                  }, 'user');
                  
                  // Only update if user has NO role, or WordPress role is HIGHER privilege
                  const shouldUpdate = roles.length === 0 || 
                    (roleHierarchy[expectedRole] || 0) > (roleHierarchy[currentHighestRole] || 0);
                  
                  if (shouldUpdate) {
                    await supabase.from('user_roles').delete().eq('user_id', authUser.id);
                    await supabase.from('user_roles').insert({
                      user_id: authUser.id,
                      role: expectedRole,
                    });
                    roles = [expectedRole];
                  } else {
                  }
                }
                
                // 🔄 SYNC: Update company_id (now uses resolvedCompanyId)
                if (resolvedCompanyId && resolvedCompanyId !== profile.company_id) {
                  const { error: companyUpdateError } = await supabase
                    .from('profiles')
                    .update({ 
                      company_id: resolvedCompanyId,
                      company_name: resolvedCompanyName || null
                    })
                    .eq('id', profile.id);
                  
                  if (companyUpdateError) {
                    console.error('⚠️ Company sync error:', companyUpdateError);
                  } else {
                    profile.company_id = resolvedCompanyId;
                  }
                }

                // Determine if profile is complete
                const profileComplete = !!(profile.company_id || resolvedCompanyId) && roles.length > 0;

                return {
                  id: profile.id,
                  email: profile.email,
                  name: profile.display_name || wpUser.name,
                  roles: roles,
                  company_id: resolvedCompanyId || profile.company_id,
                  company_name: resolvedCompanyName || (profile as any).companies?.name,
                  avatar_url: profile.avatar_url,
                  status: 'active',
                  medd_sim_user: meddSimUser,
                  profile_complete: profileComplete,
                  user_type: meddSimUserType as 'hcp' | 'non_hcp',
                  is_hcp: isHcp,
                };
              }
            }
            
            // No existing profile found, create new one
            
            // Create unique username by appending WordPress ID to avoid conflicts
            const baseUsername = wpUser.email.split('@')[0];
            const uniqueUsername = `${baseUsername}_${wpUser.id}`;
            
            const { data: newProfile, error: createError } = await supabase
              .from('profiles')
              .insert({
                wordpress_user_id: wpUser.id.toString(),
                auth_user_id: authUser.id,
                tenant_id: MASTER_TENANT_ID,
                email: wpUser.email,
                display_name: wpUser.name,
                username: uniqueUsername,
                company_id: resolvedCompanyId || null,
                company_name: resolvedCompanyName || null,
                sso_provider: 'wordpress',
              } as any)
              .select()
              .single();

            if (createError) {
              console.error('Profile creation error:', createError);
              throw createError;
            }

            // 🔐 AUTHENTICATION: Create Supabase auth session FIRST for new user
            try {
              await authenticateSupabaseUser(newProfile.email, wpUser.id.toString(), newProfile.id);
            } catch (authError) {
              console.error('❌ Supabase authentication failed for new user:', authError);
              throw authError; // Don't continue without auth
            }

            // 🔑 Get the newly created auth user
            const { data: { user: newAuthUser }, error: newAuthUserError } = await supabase.auth.getUser();

            if (newAuthUserError || !newAuthUser) {
              console.error('❌ Failed to get new auth user:', newAuthUserError);
              throw new Error('Authentication failed');
            }

            // 🔗 Link auth_user_id to new profile for RLS
            await supabase
              .from('profiles')
              .update({ auth_user_id: newAuthUser.id })
              .eq('id', newProfile.id);

            // 🆕 Assign role using medd_sim_role if provided, else use WP mapping
            let appRole: string;
            const roleToAssign = meddSimRole || wpUser.sim_role;
            if (roleToAssign) {
              appRole = roleToAssign === 'company_admin' ? 'company_admin' : 
                        roleToAssign === 'admin' ? 'admin' : 'user';
            } else {
              appRole = mapWordPressRoleToAppRole(wpUser.roles);
            }
            
            // 🔒 ENSURE role is always created
            const { error: roleError } = await supabase
              .from('user_roles')
              .insert([{
                user_id: newAuthUser.id,
                role: appRole as 'admin' | 'company_admin' | 'user',
              }]);

            if (roleError) {
              console.error('Role assignment error:', roleError);
              // Try to continue anyway with default role
              console.warn('⚠️ Continuing with potential role issue');
            } else {
            }

            // 📧 Send welcome email for first-time users
            try {
              const { error: emailError } = await supabase.functions.invoke('send-welcome-email', {
                body: {
                  email: newProfile.email,
                  name: newProfile.display_name,
                  company_name: resolvedCompanyName,
                  role: appRole,
                }
              });
              if (emailError) {
                console.warn('⚠️ Welcome email failed:', emailError);
              } else {
              }
            } catch (emailErr) {
              console.warn('⚠️ Welcome email error (non-blocking):', emailErr);
              // Don't block login if email fails
            }

            // Determine if profile is complete
            const profileComplete = !!resolvedCompanyId;

            return {
              id: newProfile.id,
              email: newProfile.email,
              name: newProfile.display_name,
              roles: [appRole],
              company_id: resolvedCompanyId || null,
              company_name: resolvedCompanyName || null,
              status: 'active',
              medd_sim_user: meddSimUser,
              profile_complete: profileComplete,
              user_type: meddSimUserType as 'hcp' | 'non_hcp',
              is_hcp: isHcp,
            };
          }

          // 🔗 Link auth_user_id to profile for RLS (if not already set)
          // Note: authUser is already available from the early authentication step
          if (!profile.auth_user_id || profile.auth_user_id !== authUser.id) {
            await supabase
              .from('profiles')
              .update({ auth_user_id: authUser.id })
              .eq('id', profile.id);
          }

          // 🔒 SECURITY: Fetch roles using auth UUID (not profile UUID)
          const { data: userRoles, error: rolesError } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', authUser.id);  // ✅ Correct auth UUID!

          if (rolesError) {
            console.error('Roles fetch error:', rolesError);
          }

          let roles = userRoles?.map(r => r.role) || [];

          // 🆕 PENDING ROLE: Check if this is a manually created user with pending_role
          // This handles users created by admins via the User Management page
          if (roles.length === 0 && (profile as any).pending_role) {
            const pendingRole = (profile as any).pending_role;
            
            try {
              // Create the user_roles entry with the pending role
              const { error: pendingRoleError } = await supabase
                .from('user_roles')
                .insert({
                  user_id: authUser.id,
                  role: pendingRole,
                });
              
              if (pendingRoleError) {
                console.error('Failed to apply pending role:', pendingRoleError);
              } else {
                roles = [pendingRole];
                
                // Clear the pending_role from profile
                await supabase
                  .from('profiles')
                  .update({ pending_role: null })
                  .eq('id', profile.id);
              }
            } catch (error) {
              console.error('Pending role application failed:', error);
            }
          }

          // 🔒 ENSURE role always exists - create if missing
          if (roles.length === 0) {
            console.warn('⚠️ No roles found for user, creating default role');
            const defaultRole = meddSimRole || 'user';
            try {
              const { error: createRoleError } = await supabase
                .from('user_roles')
                .insert({ user_id: authUser.id, role: defaultRole });
              
              if (createRoleError) {
                console.error('Failed to create default role:', createRoleError);
              } else {
              }
            } catch (e) {
              console.error('Error creating default role:', e);
            }
            roles = [defaultRole];
          }

          // 🆕 Use medd_sim_role if provided (takes priority)
          const roleToSync = meddSimRole || wpUser.sim_role;
          if (roleToSync) {
            const targetRole = roleToSync === 'company_admin' ? 'company_admin' : 
                               roleToSync === 'admin' ? 'admin' : 'user';
            
            const roleHierarchy: Record<string, number> = { 'admin': 3, 'company_admin': 2, 'user': 1 };
            const currentHighestRole = roles.reduce((highest, role) => {
              return (roleHierarchy[role] || 0) > (roleHierarchy[highest] || 0) ? role : highest;
            }, 'user');
            
            // Only update if MEDD-SIM role is higher or equal
            if ((roleHierarchy[targetRole] || 0) >= (roleHierarchy[currentHighestRole] || 0) && !roles.includes(targetRole)) {
              
              try {
                await supabase.from('user_roles').delete().eq('user_id', authUser.id);
                const { error: insertError } = await supabase.from('user_roles').insert([{
                  user_id: authUser.id,
                  role: targetRole as 'admin' | 'company_admin' | 'user',
                }]);
                
                if (insertError) {
                  console.error('MEDD-SIM role update error:', insertError);
                } else {
                  roles = [targetRole];
                }
              } catch (error) {
                console.error('MEDD-SIM role sync failed:', error);
              }
            }
          } else if (wpUser.roles) {
            // Legacy: 🔄 SYNC: Update role if WordPress permissions changed
            // PRESERVE manually assigned higher roles (e.g., company_admin assigned via database)
            const expectedRole = mapWordPressRoleToAppRole(wpUser.roles);
            
            // Role hierarchy: admin > company_admin > user
            const roleHierarchy: Record<string, number> = { 'admin': 3, 'company_admin': 2, 'user': 1 };
            const currentHighestRole = roles.reduce((highest, role) => {
              return (roleHierarchy[role] || 0) > (roleHierarchy[highest] || 0) ? role : highest;
            }, 'user');
            
            // Only update if user has NO role, or WordPress role is HIGHER privilege
            // This preserves manually assigned company_admin roles when WordPress says "user"
            const shouldUpdate = roles.length === 0 || 
              (roleHierarchy[expectedRole] || 0) > (roleHierarchy[currentHighestRole] || 0);
            
            if (shouldUpdate) {
              
              try {
                // Delete old roles using auth UUID
                await supabase.from('user_roles').delete().eq('user_id', authUser.id);
                
                // Insert new role using auth UUID
                const { error: insertError } = await supabase.from('user_roles').insert({
                  user_id: authUser.id,  // ✅ Auth UUID
                  role: expectedRole,
                });
                
                if (insertError) {
                  console.error('Role update error:', insertError);
                } else {
                  roles = [expectedRole];
                }
              } catch (error) {
                console.error('Role sync failed:', error);
              }
            } else {
            }
          }

          // 🔄 SYNC: Update company_id (now uses resolvedCompanyId for MEDD-SIM users)
          if (resolvedCompanyId && resolvedCompanyId !== profile.company_id) {
            const { error: companyUpdateError } = await supabase
              .from('profiles')
              .update({ 
                company_id: resolvedCompanyId,
                company_name: resolvedCompanyName || null
              })
              .eq('id', profile.id);
            
            if (companyUpdateError) {
              console.error('⚠️ Company sync error:', companyUpdateError);
            } else {
            }
          }

          // Update last sign-in timestamp
          await supabase
            .from('profiles')
            .update({ last_sign_in_at: new Date().toISOString() })
            .eq('id', profile.id);

          // Determine if profile is complete
          const profileComplete = !!(profile.company_id || resolvedCompanyId) && roles.length > 0;

          return {
            id: profile.id,
            supabaseAuthId: authUser.id, // Store Supabase Auth UID for RLS
            email: profile.email,
            name: profile.display_name || wpUser.name,
            roles: roles,
            company_id: resolvedCompanyId || profile.company_id,
            company_name: resolvedCompanyName || (profile as any).companies?.name,
            avatar_url: profile.avatar_url,
            status: 'active',
            medd_sim_user: meddSimUser,
            profile_complete: profileComplete,
            user_type: meddSimUserType as 'hcp' | 'non_hcp',
            is_hcp: isHcp,
          };
      } catch (supabaseError) {
        console.error('Failed to fetch user profile from Supabase:', supabaseError);
        
        // Show specific error messages based on error type
        if (supabaseError instanceof Error) {
          if (supabaseError.message.includes('multiple') || supabaseError.message.includes('more than one row')) {
            throw new Error('Multiple profiles found. Please contact support to resolve this issue.');
          } else if (supabaseError.message.includes('permission') || supabaseError.message.includes('policy')) {
            throw new Error('Database permission denied. Please contact your administrator.');
          } else if (supabaseError.message.includes('not found') || supabaseError.message.includes('no rows')) {
            throw new Error('Profile not found. Please try logging in again.');
          } else {
            throw new Error(`Profile sync failed: ${supabaseError.message}`);
          }
        }
        
        throw new Error('Unable to sync profile with database. Please try again.');
      }
    } catch (error) {
      console.error('Token validation error:', error);
      throw error;
    }
  };

  // 🆕 Load user from Supabase session directly (for white-label tenants)
  const loadUserFromSupabaseSession = async (): Promise<User | null> => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      return null;
    }

    const authUser = session.user;

    // Resolve the active tenant from the domain FIRST. It disambiguates the
    // case where one auth user has more than one profile row — whether a
    // legitimate cross-tenant identity or a legacy duplicate in the same
    // tenant (e.g. AstraZeneca's Justin Petersen had two rows sharing one
    // auth_user_id). NEVER use .maybeSingle() on profiles here: a second row
    // makes it throw PGRST116 and bounces a real user out of a valid session.
    let domainTenantId: string | null = null;
    try {
      const hostname = window.location.hostname;
      const { data: domainTenant, error: domainError } = await supabase.rpc('get_tenant_by_domain', {
        p_domain: hostname,
      });
      if (domainError) {
        // Capture, don't swallow: a failed domain lookup means we cannot
        // disambiguate duplicate profiles, so downstream must fail closed.
        console.error('❌ get_tenant_by_domain error:', domainError);
      }
      domainTenantId = domainTenant?.[0]?.tenant_id || null;
    } catch (domainLookupError) {
      console.error('❌ get_tenant_by_domain threw:', domainLookupError);
      domainTenantId = null;
    }

    // Pick deterministically via selectAuthProfile (src/lib/profileSelection):
    // the profile bearing an ACTIVE organisation assignment in the active
    // tenant wins, then a tenant-owned profile, then recency. Recency alone is
    // unsafe — a stale duplicate touched more recently than the real profile
    // would otherwise be chosen. The assignment lookup runs only when there is
    // more than one candidate, so the normal single-profile path stays cheap.
    // Fail closed: if the assignment lookup errors, resolveAuthProfile rejects
    // rather than guessing by recency (which could pick a stale duplicate).
    const lookupAssignedProfileIds = async (profileIds: readonly string[], tenantId: string) => {
      const { data: assigned, error: assignError } = await supabase
        .from('organisation_role_assignments')
        .select('profile_id')
        .in('profile_id', profileIds as string[])
        .eq('tenant_id', tenantId)
        .eq('status', 'active');
      if (assignError) {
        throw new Error(`Duplicate-profile assignment lookup failed: ${assignError.message}`);
      }
      return (assigned || []).map((row: { profile_id: string }) => row.profile_id);
    };
    const pickProfile = <T extends { id: string; tenant_id?: string | null; updated_at?: string | null }>(
      rows: T[] | null | undefined,
    ): Promise<T | null> => resolveAuthProfile(rows, { domainTenantId, lookupAssignedProfileIds });

    // Fetch profile - query by auth_user_id (the correct column linking to Supabase Auth)
    // NOT by profile.id which is different!
    let profile = null;
    let profileError = null;

    // First try by auth_user_id (the correct link)
    const { data: authLinkedProfiles, error: authError } = await supabase
      .from('profiles')
      .select(`
        *,
        companies (
          id,
          name
        )
      `)
      .eq('auth_user_id', authUser.id)
      .order('updated_at', { ascending: false });

    if (authError) {
      console.error('❌ Profile fetch by auth_user_id error:', authError);
      profileError = authError;
    } else {
      try {
        profile = await pickProfile(authLinkedProfiles);
      } catch (disambiguationError) {
        // Fail closed — refuse the session rather than resolve a duplicate wrongly.
        console.error('❌ Duplicate-profile disambiguation failed (auth_user_id):', disambiguationError);
        profileError = disambiguationError;
      }
      if ((authLinkedProfiles?.length || 0) > 1) {
        console.warn(
          `⚠️ ${authLinkedProfiles!.length} profiles share auth_user_id ${authUser.id}; using ${profile?.id} (tenant ${profile?.tenant_id}). This needs a duplicate-profile cleanup.`,
        );
      }
    }

    // Fallback: Try by email (for users who haven't linked auth_user_id yet)
    if (!profile && !profileError && authUser.email) {
      const { data: emailProfiles, error: emailError } = await supabase
        .from('profiles')
        .select(`
          *,
          companies (
            id,
            name
          )
        `)
        .eq('email', authUser.email.toLowerCase())
        .order('updated_at', { ascending: false });

      if (emailError) {
        console.error('❌ Profile fetch by email error:', emailError);
        profileError = emailError;
      } else {
        try {
          profile = await pickProfile(emailProfiles);
        } catch (disambiguationError) {
          // Fail closed — refuse the session rather than resolve a duplicate wrongly.
          console.error('❌ Duplicate-profile disambiguation failed (email):', disambiguationError);
          profileError = disambiguationError;
        }

        // Link auth_user_id for future queries
        if (profile && !profile.auth_user_id) {
          await supabase
            .from('profiles')
            .update({ auth_user_id: authUser.id })
            .eq('id', profile.id);
          profile.auth_user_id = authUser.id;
        }
      }
    }

    if (profileError) {
      console.error('❌ Profile fetch error for Supabase user:', authUser.id, profileError);
      return null;
    }
    
    if (!profile) {
      console.error('❌ No profile found for Supabase user:', authUser.id, '- email:', authUser.email);
      return null;
    }

    // The active tenant (resolved from the domain above) determines which
    // tenant_memberships row to use for role/company. Fall back to the
    // profile's own tenant only when the domain did not resolve.
    const currentTenantId: string | null = domainTenantId || profile.tenant_id;

    // Try to get role and company from tenant_memberships (multi-tenant aware)
    let roles: string[] = [];
    let membershipCompanyId: string | null = null;

    if (currentTenantId) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const supabaseAny = supabase as any;
      const { data: membership, error: membershipError } = await supabaseAny
        .from('tenant_memberships')
        .select('role, company_id, status')
        .eq('auth_user_id', authUser.id)
        .eq('tenant_id', currentTenantId)
        .maybeSingle();

      if (membershipError) {
        console.error('❌ Tenant membership lookup failed:', membershipError);
        await supabase.auth.signOut();
        return null;
      }

      if (!hasActiveTenantMembership(membership)) {
        console.warn('⛔ No active membership for the resolved tenant');
        await supabase.auth.signOut();
        return null;
      }

      roles = [membership.role];
      membershipCompanyId = membership.company_id;

      // Update membership last_login_at only after active access is proven.
      await supabaseAny
        .from('tenant_memberships')
        .update({ last_login_at: new Date().toISOString() })
        .eq('auth_user_id', authUser.id)
        .eq('tenant_id', currentTenantId)
        .eq('status', 'active');
    }

    // Fallback: if no membership found, read from user_roles (backward compatibility)
    if (roles.length === 0) {
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', authUser.id);

      roles = userRoles?.map(r => r.role) || [];
    }

    // PENDING ROLE: Check if this is a manually created user with pending_role
    if (roles.length === 0 && (profile as any).pending_role) {
      const pendingRole = (profile as any).pending_role;

      try {
        const { error: pendingRoleError } = await supabase
          .from('user_roles')
          .insert({
            user_id: authUser.id,
            role: pendingRole,
          });

        if (pendingRoleError) {
          console.error('Failed to apply pending role:', pendingRoleError);
        } else {
          roles = [pendingRole];

          // Clear the pending_role from profile
          await supabase
            .from('profiles')
            .update({ pending_role: null, sso_provider: 'supabase' })
            .eq('id', profile.id);

          // Also create a tenant_memberships row for this user
          if (currentTenantId) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const supabaseAny = supabase as any;
            await supabaseAny
              .from('tenant_memberships')
              .upsert({
                auth_user_id: authUser.id,
                tenant_id: currentTenantId,
                company_id: profile.company_id || null,
                role: pendingRole,
                status: 'active',
              }, { onConflict: 'auth_user_id,tenant_id' });
          }
        }
      } catch (error) {
        console.error('Pending role application failed:', error);
      }
    }

    // Create default role if none exists
    if (roles.length === 0) {
      const defaultRole = profile.user_type === 'platform_admin' ? 'admin' : 'user';
      await supabase.from('user_roles').insert({ user_id: authUser.id, role: defaultRole });
      roles = [defaultRole];
    }

    // Update last login timestamp (global profile)
    await supabase
      .from('profiles')
      .update({ last_login_at: new Date().toISOString() })
      .eq('id', profile.id);

    // Update tenant-specific login tracking
    if (currentTenantId) {
      try {
        await supabase.rpc('update_tenant_admin_login', {
          p_user_id: authUser.id,
          p_tenant_id: currentTenantId
        });
      } catch (rpcError) {
        console.warn('⚠️ Tenant login tracking failed:', rpcError);
      }
    }

    // PERMANENT TENANT ISOLATION: company_id MUST come from the current tenant's
    // membership, NOT from profile.company_id. profile.company_id is a legacy
    // single column that cannot hold different values per tenant, so falling back
    // to it would leak a different tenant's company into the current session and
    // silently filter the UI by the wrong tenant's company.
    // When a user has no active membership for the current tenant, company_id is
    // null — the rest of the UI must handle that gracefully (no company filter).
    const effectiveCompanyId = membershipCompanyId;

    return {
      id: profile.id,
      supabaseAuthId: authUser.id,
      email: profile.email,
      name: profile.display_name || authUser.email?.split('@')[0] || 'User',
      roles: roles,
      company_id: effectiveCompanyId || undefined,
      company_name: (profile as any).companies?.name || profile.company_name || undefined,
      avatar_url: profile.avatar_url || undefined,
      status: 'active',
      medd_sim_user: false,
      profile_complete: true,
      user_type: (profile.user_type as 'hcp' | 'non_hcp') || 'hcp',
      is_hcp: profile.user_type === 'hcp',
      tenant_id: currentTenantId || profile.tenant_id || undefined,
    };
  };

  const checkAuth = async () => {
    try {
      setLoading(true);
      setError('');

      // Check URL for token (WordPress SSO redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');

      if (tokenFromUrl) {
        
        // ⚠️ CRITICAL: Sign out previous user FIRST to prevent token contamination
        try {
          await supabase.auth.signOut();
        } catch (signOutError) {
          console.warn('⚠️ Error clearing previous session:', signOutError);
        }
        
        // Clear ALL stored credentials before processing new token
        localStorage.removeItem('medd_token');
        sessionStorage.clear();
        setUser(null);
        
        // Now store and validate the NEW token
        localStorage.setItem('medd_token', tokenFromUrl);
        window.history.replaceState({}, '', window.location.pathname);
        
        const userData = await validateToken(tokenFromUrl);
        setUser(userData);
        setSentryUser({ id: userData.id, email: userData.email, name: userData.name });
      } else {
        // Check for stored WordPress token
        const token = localStorage.getItem('medd_token');
        
        if (token) {
          // MEDD master tenant with WordPress SSO
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session) {
          }
          
          const userData = await validateToken(token);
          setUser(userData);
          setSentryUser({ id: userData.id, email: userData.email, name: userData.name });
        } else {
          // 🆕 No WordPress token - check for Supabase session (white-label tenants)
          
          const userData = await loadUserFromSupabaseSession();
          
          if (userData) {
            setUser(userData);
            setSentryUser({ id: userData.id, email: userData.email, name: userData.name });
          } else {
            setUser(null);
          }
        }
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      captureAuthError(err, { action: 'token_validation' });
      setError(err instanceof Error ? err.message : 'Authentication failed');
      setUser(null);
      clearSentryUser();
      localStorage.removeItem('medd_token');
      
      // Also clear Supabase session on auth failure
      try {
        await supabase.auth.signOut();
      } catch (e) {
        // Ignore cleanup errors
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Sign out from Supabase auth
      await supabase.auth.signOut();
    } catch (error) {
      console.error('⚠️ Supabase logout error (continuing):', error);
    } finally {
      // Clear WordPress token
      const hadWordPressToken = !!localStorage.getItem('medd_token');
      localStorage.removeItem('medd_token');
      setUser(null);
      clearSentryUser();
      
      // Only redirect to MEDD for WordPress SSO users
      // White-label tenants stay on their own domain
      if (hadWordPressToken && window.location.hostname.includes('medd.com.au')) {
        window.location.href = 'https://medd.com.au';
      } else {
        // White-label tenant: just reload to show login screen
        window.location.reload();
      }
    }
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const hasRole = (role: string): boolean => {
    return user?.roles.includes(role) || false;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return user?.roles.some(role => roles.includes(role)) || false;
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value = {
    user,
    profile: user ? { id: user.id, auth_user_id: user.supabaseAuthId || null, company_id: user.company_id || null, email: user.email, display_name: user.name } : null,
    isAuthenticated: !!user,
    loading,
    error,
    hasRole,
    hasAnyRole,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
